import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { LABOUR_STATUS_LABELS } from '@wodi/domain';
import type { LabourWardView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  formatShortAge,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
} from '@wodi/ui';

import { labourWardQuery } from '../api/maternity.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { formatInstant } from '../patients/format.ts';
import { errorText } from './common.tsx';
import styles from './maternity.module.css';

// Maternity (docs/plans/phase2-maternity-mch.md §6): who is in labour at this facility. A
// record on a visit the reader may not see shows only its place, as "Restricted record".

type WardRow = LabourWardView['rows'][number];

function place(row: WardRow): string {
  if (row.wardName === null) return 'Not in a ward';
  return row.bedLabel === null ? `${row.wardName}, no bed` : `${row.wardName}, bed ${row.bedLabel}`;
}

export function LabourWardPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const ward = useQuery(labourWardQuery);
  if (session === null || !can(session, 'maternity.read')) {
    return (
      <Notice tone="info">
        You don't have permission to see maternity records. Ask an administrator if you need it.
      </Notice>
    );
  }
  return (
    <div className={styles.page}>
      <PageHeader
        title="Maternity"
        description="Women in labour here. Open a labour record from the stay or the patient's maternity page."
      />
      {online ? null : (
        <Notice tone="info">No connection to Wodi. The list updates when it is back.</Notice>
      )}
      <Section title="In labour">
        {ward.isPending ? <LoadingBlock label="Loading the labour ward" rows={4} /> : null}
        {ward.isError ? (
          <Notice
            tone="error"
            action={<Button onPress={() => void ward.refetch()}>Try again</Button>}
          >
            {errorText(ward.error, 'The labour ward could not be loaded. Try again.')}
          </Notice>
        ) : null}
        {ward.isSuccess ? (
          <Table flat aria-label="In labour">
            <TableHeader>
              <Column isRowHeader>Patient</Column>
              <Column>Place</Column>
              <Column>Labour record opened</Column>
              <Column>Last observation</Column>
              <Column>State</Column>
            </TableHeader>
            <TableBody
              items={ward.data.rows.map((r) => ({ ...r, id: r.labourRecordId }))}
              renderEmptyState={() =>
                'No one in labour. Open a labour record from the stay of an admitted patient.'
              }
            >
              {(row) => (
                <Row id={row.id}>
                  <Cell>
                    {row.patient === null ? (
                      'Restricted record'
                    ) : (
                      <Link to="/labour/$labourRecordId" params={{ labourRecordId: row.id }}>
                        {`${row.patient.displayName}, ${formatShortAge(row.patient.age, false)}`}
                      </Link>
                    )}
                    {row.patient === null ? null : (
                      <span
                        className={`${styles.meta} wodi-id`}
                      >{` ${row.patient.patientNumber}`}</span>
                    )}
                  </Cell>
                  <Cell>{place(row)}</Cell>
                  <Cell>
                    <span className={styles.numeric}>{formatInstant(row.openedAt)}</span>
                  </Cell>
                  <Cell>
                    <span className={styles.numeric}>
                      {row.lastObservationAt === null
                        ? 'None yet'
                        : formatInstant(row.lastObservationAt)}
                    </span>
                  </Cell>
                  <Cell>
                    <Tag state="active">{LABOUR_STATUS_LABELS[row.status]}</Tag>
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        ) : null}
      </Section>
    </div>
  );
}
